import { type Etapa, formatearRevision } from "@gestiondoc/shared";
import type { RevisionDetalle } from "../tipos.ts";

/**
 * Los textos de la firma de una revisión (el botón de la pantalla, el título del modal y la
 * advertencia de consecuencias), que dependen de la etapa y de si la ruta lleva revisor.
 */

/** Lo mínimo de una revisión para saber quién va en su ruta. */
type ConRuta = { ruta: { etapa: string; usuario: unknown }[] };

const TEXTOS: Record<Etapa, { titulo: string; boton: string }> = {
  elaboro: { titulo: "Firmar y enviar a revisión", boton: "Firmar y enviar" },
  reviso: { titulo: "Firmar como revisado", boton: "Firmar como revisado" },
  aprobo: { titulo: "Firmar y aprobar", boton: "Firmar y aprobar" },
};

/**
 * Ruta corta (2026-10-29): hay quien aprueba pero nadie revisa; la firma de quien elabora va
 * directo a aprobación. La ruta trae las tres etapas siempre, con `usuario` nulo en las que no tiene.
 */
export function sinRevisor(rev: ConRuta): boolean {
  const asignada = (etapa: string) => rev.ruta.some((p) => p.etapa === etapa && p.usuario);
  return asignada("aprobo") && !asignada("reviso");
}

/** El título del modal de firma y el texto de su botón para esta etapa y esta ruta. */
export function textosDeFirma(etapa: Etapa, rev: ConRuta): { titulo: string; boton: string } {
  return etapa === "elaboro" && sinRevisor(rev)
    ? { titulo: "Firmar y enviar a aprobación", boton: "Firmar y enviar" }
    : TEXTOS[etapa];
}

/** El texto del botón que abre el modal de firma. */
export function textoBotonFirma(etapa: Etapa, rev: ConRuta): string {
  return textosDeFirma(etapa, rev).titulo;
}

/** La advertencia de consecuencias del mockup: qué pasa con la revisión al firmar. */
export function consecuenciaDe(
  etapa: Etapa,
  rev: RevisionDetalle,
  vigente: RevisionDetalle | null,
): string {
  const n = formatearRevision(rev.numero);
  const revisor = rev.ruta.find((p) => p.etapa === "reviso")?.usuario?.nombre ?? "el revisor";
  const aprobador = rev.ruta.find((p) => p.etapa === "aprobo")?.usuario?.nombre ?? "el aprobador";
  switch (etapa) {
    case "elaboro":
      return sinRevisor(rev)
        ? `Al firmar, la Rev. ${n} queda esperando la aprobación de ${aprobador}. Si cambias el archivo después, tendrás que firmar de nuevo.`
        : `Al firmar, la Rev. ${n} pasa a revisión de ${revisor}. Si cambias el archivo después, tendrás que firmar de nuevo.`;
    case "reviso":
      return `Al firmar, la Rev. ${n} queda esperando la aprobación de ${aprobador}.`;
    case "aprobo":
      return vigente
        ? `Al firmar, la Rev. ${formatearRevision(vigente.numero)} pasa a obsoleta y la Rev. ${n} queda vigente. Los cambios posteriores requerirán una Rev. ${formatearRevision(rev.numero + 1)}.`
        : `Al firmar, la Rev. ${n} queda vigente. Los cambios posteriores requerirán una Rev. ${formatearRevision(rev.numero + 1)}.`;
  }
}
